import {
  ChangeEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import React from "react";

interface SelectionContextType<T> {
  selected: ReadonlyArray<number | string>;
  selectedLength: number;
  isSelected: (item: T) => boolean;
  handleItemChange: (item: T, checked: boolean) => void;
  isAllSelected: boolean;
  isAllIndeterminate: boolean;
  handleSelectAllChange: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  unselectAll: () => void;
  setSelectedIds: (ids: ReadonlyArray<number | string>) => void;
  items: Readonly<T[]>;
}

export function createSelectionContext<T>() {
  console.log("rendering creating selection context");
  const Context = createContext<SelectionContextType<T> | null>(null);
  const useSelection = () => {
    const ctx = useContext(Context);
    if (!ctx)
      throw new Error("useSelection must be used within SelectionProvider");
    return ctx;
  };

  const SelectionProvider: React.FC<{
    items: Readonly<T[]>;
    getId: (item: T) => number | string;
    isSelectable?: (item: T) => boolean;
    children: React.ReactNode;
  }> = ({ items, children, getId, isSelectable = () => true }) => {
    const [selectedIds, setSelectedIds] = useState<
      ReadonlyArray<number | string>
    >([]);

    const selectedKey = useMemo(
      () => [...selectedIds].sort().toString(),
      [selectedIds],
    );

    const itemsKey = useMemo(
      () =>
        items
          .map((i) => getId(i))
          .sort()
          .toString(),
      [items],
    );

    useEffect(() => {
      // When the list of items changes, keep any selections that still exist
      setSelectedIds((prev) =>
        prev.filter((id) => items.some((item) => getId(item) === id)),
      );
    }, [itemsKey]);

    const selectableItems = useMemo(() => {
      if (isSelectable && typeof isSelectable === "function") {
        return items.filter(isSelectable);
      }
      return items;
    }, [itemsKey, isSelectable]);

    console.log("rendering selection provider", selectedKey, itemsKey);
    const isSelected = (item: T) => selectedIds.includes(getId(item));

    const handleItemChange = (item: T, checked: boolean) => {
      const id = getId(item);
      setSelectedIds((prev) =>
        checked ? [...prev, id] : prev.filter((i) => i !== id),
      );
    };

    const handleSelectAllChange = (
      _: ChangeEvent<HTMLInputElement>,
      checked: boolean,
    ) => {
      setSelectedIds(checked ? items.filter(isSelectable).map(getId) : []);
    };

    const unselectAll = () => {
      if (selectedIds && selectedIds.length > 0) setSelectedIds([]);
    };

    const isAllSelected =
      selectableItems.length > 0 &&
      selectedIds.length === selectableItems.length;

    const isAllIndeterminate =
      selectedIds.length > 0 && selectedIds.length < selectableItems.length;

    const selectedLength = selectedIds.length;

    const value = useMemo(
      () => ({
        selected: selectedIds,
        selectedLength,
        isSelected,
        handleItemChange,
        isAllSelected,
        isAllIndeterminate,
        handleSelectAllChange,
        unselectAll,
        setSelectedIds,
        items,
      }),
      [selectedKey, itemsKey],
    );
    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  return { useSelection, SelectionProvider };
}
